import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Database, Globe, RefreshCw, FileText } from 'lucide-react';

const Privacy = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const sections = [
    {
      icon: Lock,
      title: "Information Collection",
      description: "We respect your privacy and are committed to protecting it. No personal information is collected when you visit our website, and we do not track, store, or retain user data. No cookies or tracking technologies are used."
    },
    {
      icon: Database,
      title: "Information Usage",
      description: "Since no data is collected, we do not process, share, or sell personal data, nor do we use information for marketing or advertising purposes."
    },
    {
      icon: Shield,
      title: "Data Storage",
      description: "We do not store any data about our users, and no personal information is saved on our servers."
    },
    {
      icon: Globe,
      title: "Third-Party Services",
      description: "Our website may link to external sites. We are not responsible for their privacy practices and recommend reviewing their privacy policies."
    },
    {
      icon: Shield,
      title: "Security Measures",
      description: "We ensure a secure browsing experience, even though we do not collect any data."
    },
    {
      icon: RefreshCw,
      title: "Policy Updates",
      description: "This policy may be updated periodically. Changes will be reflected on this page with an updated effective date."
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <FileText className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Privacy Notice</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Last Updated: {currentDate}
          </motion.p>
        </div>

        {/* Privacy Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <section.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{section.title}</h3>
              </div>
              <p className="text-muted leading-relaxed">{section.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Additional Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Questions About Our Privacy Notice?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              If you have any questions about our privacy practices or this notice, please contact our privacy team.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button 
                onClick={() => window.location.href = 'mailto:contact@astronshield.com'} 
                className="btn-primary"
              >
                Contact Privacy Team
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;